import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, JSX } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import ProjectDropdown from "../../../organisms/ProjectDropdown";
import clsx from "clsx";
import type { Message } from "./ChatDrawerSocket";
import type { ProjectItem } from "../../../../types/project";
import HistoryModal from "./HIstoryModal";
import type { HistoryModalConfig } from "./HIstoryModal";
import MessageList from "./MessageList";
import { ai_text_icon as defaultAiTextIcon, open_in_full_icon, non_expand, refresh_prompt, curcular_attach_file } from "../../../../assets/images/Images";
import Loader from "../../../atoms/Loader";
import useChatMessageStore from "../../../../store/chatMessagesStore";
import useChatSessionStore from "../../../../store/chatSessionStore";
import ProjectSidebar from "./ProjectSidebar";
import { toast } from "react-toastify";
import type { ProjectSidebarConfig } from "./ProjectSidebar";
import useChatbotDrawer from "../../../../store/useChatbotDrawer";
import EllipsisText from "../../../../components/atoms/Ellipsis";
import React from "react";
import WorkWebToggle from "../../../atoms/WorkWebToggle";
import "../../../../styles/component-tailwind.css";
import styles from "../chatDrawer.module.css";
import { marked } from "marked";
import PreprocessAndRenderLatex from "../../../../../ProcessRenderLatex";
import FileUploadModal from "./FileUploadModal";
import useFileUploadStore from "../../../../store/fileUploadStore";
import type { UploadedFile } from "../../../../store/fileUploadStore";
import { uploadFiles, validateFiles, deleteFile, isFileTypeAllowed, ALLOWED_FILE_EXTENSIONS } from "../../../../services/fileUploadService";
import { validateFilesForUpload, filterDuplicateFiles } from "../../../../utils/fileValidation";
import AttachedFilesList from "../../../atoms/AttachedFilesList/AttachedFilesList";
import type { GoogleTokenData } from "../../../../services/googleDriveService";
import useClipboardPaste from "../../../../hooks/useClipboardPaste";
import useDragAndDrop from "../../../../hooks/useDragAndDrop";
import DragDropOverlay from "../../../atoms/DragDropOverlay.jsx"

// Max auto-grow height of the chat textarea (~6 lines); beyond this it scrolls
const CHAT_INPUT_MAX_HEIGHT = 150;

interface AfterExpandProps {
  /** Optional custom component to render in suggestions body */
  customSuggestionComponent?: React.ReactNode;
  historyModalRef: React.MutableRefObject<{
    fetchSessions: (search: string) => void;
  } | null>;
  setShowHistory: (show: boolean) => void;
  handleSelectSessionFromHistory: any;
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setShowDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  selectedProject: string | null;
  handleProjectChange: (projectId: string, project_name: string) => void;
  showDropdown: boolean;
  findProjectNameById: (projectId: string) => string;
  handleExpand: () => void;
  onClose: () => void;
  totalSessionCount?: number | null;
  currentIndexInSession?: number;
  handleNavigate: (direction: "up" | "down") => void;
  isNavigating?: boolean;
  currentIndexId?: number;
  navigationError: any;
  showLoadMore?: boolean;
  user?: any;
  AIAssistantName?: string;
  messages: any[];
  inputValue: string;
  suggestionPrompts?: string[];
  aufgangAnimationUrl?: string;
  aufgangAnimationAlt?: string;
  aufMinUrl?: string;
  aufMinAlt?: string;
  defaultProjectPlaceholder?: string;
  workWebToggleActiveBgColor?: string;
  workWebToggleInactiveTextColor?: string;
  workWebToggleTextColor?: string;
  aiTextIconUrl?: string;
  openInFullIconUrl?: string;
  nonExpandIconUrl?: string;
  historyIconUrl?: string;
  chatCloseIconUrl?: string;
  chatbotDownArrowUrl?: string;
  chatbotUpArrowUrl?: string;
  replyChatIconUrl?: string;
  chatMicOffIconUrl?: string;
  miconUrl?: string;
  chatbotMicIconUrl?: string;
  chatSendIconUrl?: string;
  chatSendIconWhiteUrl?: string;
  logoVideoUrl?: string;
  sendButtonColor?: string;
  sendButtonHoverColor?: string;
  sendButtonDisabledColor?: string;
  sendButtonTextColor?: string;
  handleSuggestionClick: (prompt: string) => void;
  chatContainerRef: React.RefObject<HTMLDivElement | null>;
  handleChatScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  showScrollButton: boolean;
  showScrollButtonRef?: React.MutableRefObject<boolean>;
  handleCopy: (content: string, id: number) => void;
  isUpdatingSpeakingStateRef?: React.MutableRefObject<boolean>;
  handleIconMouseEnter: (
    id: number,
    type: "copy" | "Like" | "speaker" | "switch_project"
  ) => void;
  generateInitials?: (firstName: string, lastName: string) => string;
  replyMessage?: Message | null;
  highlightProjectNames: (text: string) => string | (string | JSX.Element)[];
  handleReply: (
    msg: any,
    projectId: string,
    messageId: string,
    indexId: number
  ) => void;
  handleSpeakerClick: (id: number, content: string) => void;
  handleDropdownToggle: (messageId: number) => void;
  handleIconMouseLeave: () => void;
  openDropdownMessageId: number | null;
  setOpenDropdownMessageId: React.Dispatch<React.SetStateAction<number | null>>;
  buttonRef?: React.RefObject<HTMLButtonElement | null>;
  dropdownRef?: React.RefObject<HTMLDivElement | null>;
  hoveredButton?: { id: number; type: string } | null;
  copiedMessageId?: number | null;
  projectsList?: {
    projects: ProjectItem[];
    isNewFormat: boolean;
    originalResponse: any;
  } | null;
  agentData?: ProjectItem[]; // Optional agent data to use instead of fetching
  setSelectedProject: (text: string) => void | null;
  messagesEndRef?: React.RefObject<HTMLDivElement | null>;
  handleLoadMoreUp?: () => void;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
  handleInputChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  isListening?: boolean;
  setIsListening?: React.Dispatch<React.SetStateAction<boolean>>;
  listeningDots?: string;
  setListeningDots?: React.Dispatch<React.SetStateAction<string[]>>;
  handleInputKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  handleSendMessage?: () => void;
  handleMicClick?: () => void;
  setIsMicHovered?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSendHovered?: React.Dispatch<React.SetStateAction<boolean>>;
  isMicHovered?: boolean;
  isSendHovered?: boolean;
  handleSend?: () => void;
  isSearchFieldNeeded?: boolean;
  showHistory?: boolean;
  handleShowHistory?: () => void;
  showLoadMoreDown?: boolean;
  tabOneName?: string;
  chatMessageIsLoading?: boolean;
  historyModalConfig?: HistoryModalConfig;
  projectSidebarConfig?: ProjectSidebarConfig;
  loadMoreMessagesText?: string;
  loadingMoreMessagesText?: string;
  loadMoreBottomMessages?: (indexId?: number) => Promise<void> | void;
  isBottomLoading?: boolean;
  isHistoryIconVisible?: boolean;
  isProjectDropdownVisible?: boolean;
  isProjectChangeIconVisible?: boolean;
  // Color customization props for FeedbackForm
  feedbackBackgroundColor?: string;
  feedbackBorderColor?: string;
  feedbackTextColor?: string;
  feedbackButtonColor?: string;
  feedbackButtonHoverColor?: string;
  feedbackPlaceholderColor?: string;
  thumbUpIcon?: string;
  thumbDownIcon?: string;
  // Input placeholder customization props
  workModePlaceholder?: string;
  webModePlaceholder?: string;
  listeningPlaceholder?: string;
  aiThinkingPlaceholder?: string;
  isWorkWebEnableForAll?: boolean;
  workLabel?: string;
  webLabel?: string;
  isWebAllowed?: boolean;
  isExpandableButtonEnable?: boolean;
  // Google Drive integration props (passed from parent to avoid redundant stores)
  /** Google token data from parent component */
  googleTokenData?: GoogleTokenData | null;
  /** Auth token for Google Drive API calls */
  googleAuthToken?: string;
  /** Callback to submit Google token to backend after OAuth */
  onGoogleTokenSubmit?: (tokenData: { google_token: GoogleTokenData }) => void;
  /** Callback to fetch Google token from backend */
  onGoogleTokenFetch?: () => void;
  /** Callback when file IDs are selected from Google Drive (for embeddings) */
  onGoogleDriveFileIds?: (fileIds: string[]) => void;
  /** Whether to show Google Drive option in file upload modal */
  showGoogleDriveOption?: boolean;
  stopReconnectionSocket?: boolean;
}

const BeforeExpand = ({
  historyModalRef,
  setShowHistory,
  handleSelectSessionFromHistory,
  defaultProjectPlaceholder = "Ask AI",
  isExpanded,
  setIsExpanded,
  setShowDropdown,
  selectedProject,
  // handleProjectChange,
  showDropdown,
  findProjectNameById,
  handleExpand,
  onClose,
  isSearchFieldNeeded,
  totalSessionCount = null,
  currentIndexInSession = 0,
  handleNavigate,
  isNavigating = false,
  // currentIndexId = undefined,
  navigationError = false,
  // showLoadMore = false,
  user,
  AIAssistantName = "My AI",
  messages = [],
  inputValue,
  suggestionPrompts = [],
  aufgangAnimationUrl = "/placeholder.svg",
  aufgangAnimationAlt = "aufgangAi",
  aufMinUrl = "/placeholder.svg",
  aufMinAlt = "aufgangAi",
  logoVideoUrl,
  openInFullIconUrl = open_in_full_icon,
  nonExpandIconUrl = non_expand,
  historyIconUrl = "/placeholder.svg",
  chatCloseIconUrl = "/placeholder.svg",
  chatbotDownArrowUrl = "/placeholder.svg",
  chatbotUpArrowUrl = "/placeholder.svg",
  replyChatIconUrl = "/placeholder.svg",
  chatMicOffIconUrl = "/placeholder.svg",
  miconUrl = "/placeholder.svg",
  chatbotMicIconUrl = "/placeholder.svg",
  chatSendIconUrl = "/placeholder.svg",
  chatSendIconWhiteUrl = "/placeholder.svg",
  sendButtonColor = "#00b2c1",
  sendButtonHoverColor = "#0099a0",
  sendButtonDisabledColor = "#f7f7f7",
  sendButtonTextColor = "#ffffff",
  workWebToggleActiveBgColor = "#e8e5e5",
  workWebToggleInactiveTextColor = "#CCCCCC",
  workWebToggleTextColor = "#0099a0",
  aiTextIconUrl = defaultAiTextIcon,
  handleSuggestionClick,
  customSuggestionComponent, // new prop for custom component
  chatContainerRef,
  handleChatScroll,
  showScrollButton,
  showScrollButtonRef,
  handleCopy,
  isUpdatingSpeakingStateRef,
  handleIconMouseEnter,
  generateInitials = (firstName: string, lastName: string) => {
    return (
      firstName.charAt(0).toUpperCase() +
      (lastName ? lastName.charAt(0).toUpperCase() : "")
    );
  },
  highlightProjectNames,
  handleReply,
  handleSpeakerClick,
  handleDropdownToggle,
  handleIconMouseLeave,
  openDropdownMessageId = null,
  setOpenDropdownMessageId,
  buttonRef = undefined,
  dropdownRef = undefined,
  hoveredButton = null,
  copiedMessageId = null,
  projectsList = null,
  agentData,
  setSelectedProject,
  messagesEndRef = undefined,
  // handleLoadMoreUp = () => {},
  // inputRef = undefined,
  handleInputChange = () => { },
  isListening = false,
  // setIsListening = () => {},
  listeningDots,
  // setListeningDots = () => {},
  handleInputKeyDown = () => { },
  // handleSendMessage = () => {},
  handleMicClick = () => { },
  setIsMicHovered = () => { },
  // setIsSendHovered = () => {},
  isMicHovered = false,
  // isSendHovered = false,
  handleSend = () => { },
  showHistory = false,
  handleShowHistory = () => { },
  // showLoadMoreDown = false,
  chatMessageIsLoading = false,
  // tabOneName,
  historyModalConfig = {},
  projectSidebarConfig = {},
  loadMoreMessagesText = "Load more messages",
  loadingMoreMessagesText = "Loading more messages...",
  loadMoreBottomMessages,
  isBottomLoading,
  isHistoryIconVisible = true,
  isProjectDropdownVisible = true,
  isProjectChangeIconVisible = true,
  // Color props for FeedbackForm
  feedbackBackgroundColor,
  feedbackBorderColor,
  feedbackTextColor,
  feedbackButtonColor,
  feedbackButtonHoverColor,
  feedbackPlaceholderColor,
  thumbUpIcon,
  thumbDownIcon,
  // Input placeholder props with defaults
  workModePlaceholder = "Ask about emails or files…",
  webModePlaceholder = "Search the web…",
  listeningPlaceholder = "Listening",
  aiThinkingPlaceholder = "AI is thinking...",
  isWorkWebEnableForAll = false,
  workLabel,
  webLabel,
  isWebAllowed,
  isExpandableButtonEnable = true,
  // Google Drive props
  googleTokenData,
  googleAuthToken,
  onGoogleTokenSubmit,
  onGoogleTokenFetch,
  onGoogleDriveFileIds,
  showGoogleDriveOption = true,
  stopReconnectionSocket
}: AfterExpandProps) => {
  const {
    chatMessageAppendIsLoading,
    clearAndSetChatMessages,
    refreshChatMessages,
    appendAtEndChatMessages,
    topCurrentIndexId,
    bottomCurrentIndexId,
    appendAtBottomChatMessages,
    chatMessagePrependIsLoading,
    chatMessageError,
    showSuggestions,
    replyMessage,
    setReplyMessage,
    setReplyToIndexId,
    setReplyToMessageId,
    IsWebEnabled,
    setIsWebEnabled,
    isAIResponding, // Add this new state
  } = useChatMessageStore();



  const loadMoreBottomMessagesHandler =
    loadMoreBottomMessages ?? appendAtBottomChatMessages;
  const bottomLoading =
    typeof isBottomLoading === "boolean"
      ? isBottomLoading
      : chatMessagePrependIsLoading;

  const {
    dropdownSelectedProjectId,
    setDropdownSelectedProjectId,
    setSessionIsOn,
    sessionIsOn,
    setSelectedSessionId,
    setShowNavigationArrows,
    showNavigationArrows,
  } = useChatSessionStore();

  const { showChatDrawer } = useChatbotDrawer();
  // const setShowChatDrawer = useChatbotDrawer((state) => state.setShowChatDrawer); // Not currently used

  const inputRef2 = useRef<HTMLTextAreaElement>(null);
  // const logoVideoRef = useRef<HTMLVideoElement | null>(null);
  const wasLoadingPreviousMessages = useRef(false);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  // File upload state
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const {
    attachedFiles,
    addAttachedFile,
    removeAttachedFile,
    updateFileUploadStatus,
    setRagCollectionNames,
    removeRagCollectionName,
  } = useFileUploadStore();



  // Check if there are any successfully uploaded files
  const hasUploadedFiles = attachedFiles.some(
    (file) => file.uploadStatus === "completed"
  );

  const hasFileUploading = attachedFiles.some(
    (file) => file.uploadStatus === "uploading"
  );

  const sendButtonStyles: CSSProperties = {};
  if (sendButtonColor) {
    (sendButtonStyles as any)["--send-button-bg"] = sendButtonColor;
  }
  if (sendButtonHoverColor) {
    (sendButtonStyles as any)["--send-button-hover-bg"] = sendButtonHoverColor;
  }
  if (sendButtonDisabledColor) {
    (sendButtonStyles as any)["--send-button-disabled-bg"] =
      sendButtonDisabledColor;
  }
  if (sendButtonTextColor) {
    (sendButtonStyles as any)["--send-button-fg"] = sendButtonTextColor;
  }

  const [page, setPage] = useState(0);
  const suggestionsPerPage = 4;

  const currentSuggestions = suggestionPrompts.slice(page * suggestionsPerPage, (page + 1) * suggestionsPerPage);


  const handleInputChangeWithRefresh = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    handleInputChange(e);
  };
  // This is the key loading state for when AI is generating responses
  const isInputDisabled = isAIResponding;

  // Auto-grow the textarea with its content (starts at one row), then scroll past the cap.
  // Re-run on expand/collapse since the width change alters line wrapping.
  useLayoutEffect(() => {
    const textarea = inputRef2.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    // scrollHeight excludes borders; add them back so border-box sizing doesn't trigger a scrollbar
    const borderHeight = textarea.offsetHeight - textarea.clientHeight;
    textarea.style.height = `${Math.min(textarea.scrollHeight + borderHeight, CHAT_INPUT_MAX_HEIGHT)}px`;
  }, [inputValue, isExpanded]);



  const closeReply = () => {
    setReplyMessage(null);
    setReplyToMessageId(null);
    setReplyToIndexId(null);
  };

  // Track when we're loading previous messages to prevent scrolling
  useEffect(() => {
    if (chatMessageAppendIsLoading) {
      wasLoadingPreviousMessages.current = true;
    } else if (wasLoadingPreviousMessages.current) {
      // Just finished loading previous messages, reset after a brief delay
      setTimeout(() => {
        wasLoadingPreviousMessages.current = false;
      }, 100);
    }
  }, [chatMessageAppendIsLoading]);

  useEffect(() => {
    // Scroll to bottom when expanding or when new messages are added at the bottom
    // Skip scrolling when loading previous messages or just finished loading them
    // Also skip scrolling when only updating speaking state
    if (
      messagesEndRef &&
      messagesEndRef.current &&
      !chatMessageAppendIsLoading &&
      !wasLoadingPreviousMessages.current &&
      !(isUpdatingSpeakingStateRef?.current) &&
      // Don't follow the stream down if the user scrolled up to read.
      !(showScrollButtonRef?.current)
    ) {
      // Use longer timeout for non-expanded state (dev/qat environments need more time)
      const timeout = isExpanded ? 0 : 100;
      setTimeout(() => {
         if (messagesEndRef.current && !showScrollButtonRef?.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "auto" });
        }
      }, timeout);
    }
  }, [isExpanded, messages, chatMessageAppendIsLoading, isUpdatingSpeakingStateRef]);

  useEffect(() => {
    if (inputRef2 && inputRef2.current) {
      setTimeout(() => {
        if (inputRef2.current) {
          if (window.innerWidth > 640) {
            inputRef2.current.focus();
          }
        }
      }, 0);
    }
  }, [showChatDrawer]);

  useEffect(() => {
    if (inputRef2 && inputRef2.current && !isInputDisabled) {
      if (window.innerWidth > 640) {
        inputRef2.current.focus();
      }
    }
  }, [isExpanded, showChatDrawer, replyMessage, showHistory, isInputDisabled]);


  const TabToBottom = () => {

    if (messagesEndRef && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  };

  const handleScrollClick = () => {
    TabToBottom();
  };

  // File upload handlers
  const handleFileSelect = async (files: File[] ) => {
    // Validate file types first (most specific and actionable error)
    const invalidTypeFiles = files.filter((file) => !isFileTypeAllowed(file));
    if (invalidTypeFiles.length > 0) {
      const invalidNames = invalidTypeFiles.map((f) => f.name).join(', ');
      const allowedTypes = ALLOWED_FILE_EXTENSIONS.map((ext) => ext.toUpperCase()).join(', ');
      toast.error(`Invalid file type(s): ${invalidNames}. Allowed types: ${allowedTypes}`);
      return;
    }

    // Check for duplicate files (already selected/attached)
    const existingFileNames = attachedFiles.map((f) => f.name);
    const { uniqueFiles, duplicateFiles } = filterDuplicateFiles(files, existingFileNames);

    // Show toast error for duplicate files
    if (duplicateFiles.length > 0) {
      const duplicateNames = duplicateFiles.map((f) => f.name).join(', ');
      toast.error(`File(s) already selected: ${duplicateNames}`);
    }

    // If all files are duplicates, return early
    if (uniqueFiles.length === 0) {
      return;
    }

    // Continue with non-duplicate files
    files = uniqueFiles;

    // Check existing files count
    const currentFileCount = attachedFiles.length;
    const remainingSlots = 3 - currentFileCount;

    if (files.length > remainingSlots) {
      toast.error(`Maximum 3 files are allowed.`);
      return;
    }

    // Calculate total size including existing files
    const existingFilesSize = attachedFiles.reduce((sum, file) => sum + file.size, 0);
    const newFilesSize = files.reduce((sum, file) => sum + file.size, 0);
    const totalSizeMB = (existingFilesSize + newFilesSize) / (1000 * 1000);

    if (totalSizeMB > 25) {
      toast.error(`Total file size must be less than 25 MB. Current total size: ${totalSizeMB.toFixed(2)} MB`);
      return;
    }

    // Validate new files (this includes type check again for completeness)
    const validation = validateFiles(files, remainingSlots, 25 - (existingFilesSize / (1024 * 1024)));
    if (!validation.isValid) {
      // TODO: Show error toast/notification
      console.error(validation.error);
      toast.error(validation.error); // Temporary alert, replace with proper notification
      return;
    }

    // Validate files for corruption and password protection before upload
    const { validFiles, invalidFiles } = await validateFilesForUpload(files);

    // Show error toasts for invalid files
    if (invalidFiles.length > 0) {
      invalidFiles.forEach(({ file, reason }) => {
        toast.error(`${file.name}: ${reason}`);
      });
    }

    // If no valid files remain after validation, return early
    if (validFiles.length === 0) {
      return;
    }

    // Create uploaded file objects (only for valid files)
    const newFiles: UploadedFile[] = validFiles.map((file) => ({
      id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadStatus: "pending",
    }));

    // Add files to store
    newFiles.forEach((file) => {
      addAttachedFile(file);
    });

    // Set all files to uploading status
    newFiles.forEach((file) => {
      updateFileUploadStatus(file.id, "uploading");
    });

    try {
      // Upload all valid files together
      const fileObjects = newFiles.map((f) => f.file);
      const response = await uploadFiles(fileObjects, IsWebEnabled, dropdownSelectedProjectId);

      // Store collection names for WebSocket
      if (response.collection_names && response.collection_names.length > 0) {
        setRagCollectionNames(response.collection_names);
      }

      // Map response files back to uploaded files by name
      const responseFileMap = new Map(
        response.files.map((f) => [f.file_name, f])
      );

      // Map failed files by name for quick lookup
      const failedFilesMap = new Map(
        response.failed_files?.map((f) => [f.name, f]) || []
      );

      // Update each file's status based on response
      newFiles.forEach((uploadedFile) => {
        const responseFile = responseFileMap.get(uploadedFile.name);
        const failedFileInfo = failedFilesMap.get(uploadedFile.name);

        // Check if file status is "success" in response
        if (responseFile && responseFile.status === "success") {
          // Update file with collection_name and URL
          updateFileUploadStatus(
            uploadedFile.id,
            "completed",
            responseFile.url,
            undefined,
            responseFile.collection_name || undefined
          );
        } else {
          // File failed - get error message from failed_files array
          updateFileUploadStatus(
            uploadedFile.id,
            "error",
            undefined,
            failedFileInfo?.message || "Upload failed"
          );
        }
      });
    } catch (error: any) {
      // Extract error message from API response if available
      const apiErrorMessage = error?.response?.data?.message;

      // Update all files to error status
      newFiles.forEach((uploadedFile) => {
        updateFileUploadStatus(
          uploadedFile.id,
          "error",
          undefined,
          apiErrorMessage || (error instanceof Error ? error.message : "Upload failed")
        );
      });
    }
  };

  const handleRemoveFile = async (id: string) => {
    // Find the file to get its collection_name
    const fileToRemove = attachedFiles.find((f) => f.id === id);

    if (fileToRemove?.collection_name) {
      try {
        // Call delete API
        await deleteFile(fileToRemove.collection_name);

        // Remove collection_name from ragCollectionNames
        removeRagCollectionName(fileToRemove.collection_name);

        // Remove file from store
        removeAttachedFile(id);
      } catch (error) {
        console.error("Error deleting file:", error);
        // Still remove from UI even if API call fails
        removeAttachedFile(id);
        // Remove collection_name from ragCollectionNames even if API fails
        if (fileToRemove.collection_name) {
          removeRagCollectionName(fileToRemove.collection_name);
        }
      }
    } else {
      // If no collection_name, just remove from store
      removeAttachedFile(id);
    }
  };

  // Handle invalid file types pasted from clipboard
  const handleInvalidPastedFileType = (files: File[]) => {
    const invalidNames = files.map((f) => f.name).join(', ');
    const allowedTypes = ALLOWED_FILE_EXTENSIONS.map((ext) => ext.toUpperCase()).join(', ');
    toast.error(`Invalid file type(s): ${invalidNames}. Allowed types: ${allowedTypes}`);
  };

  // Set up clipboard paste functionality for file uploads
  // This allows users to paste files directly from clipboard into the chat input
  useClipboardPaste({
    enabled: !isInputDisabled,
    onFilesPasted: handleFileSelect,
    onInvalidFileType: handleInvalidPastedFileType,
    targetRef: inputRef2,
  });

  // Set up drag and drop functionality for file uploads
  // This allows users to drag and drop files into the input container
  const { isDragging, dropZoneProps } = useDragAndDrop({
    enabled: !isInputDisabled,
    onFilesDrop: handleFileSelect,
    onInvalidFileType: handleInvalidPastedFileType,
    dropZoneRef: inputContainerRef,
  });


  return (
    <>
      <div className={`${isExpanded ? "flex h-[100dvh]" : ""}`}>
        {isExpanded ? (
          <div
            className={`w-[35%] min-w-[320px] border-r border-r-1 border-gray-300 shadow-lg box-border
 ${isExpanded ? "" : "hidden"}`}
          >
            <ProjectSidebar
              isSearchFieldNeeded={isSearchFieldNeeded}
              aiAssistantName={AIAssistantName}
              ref={historyModalRef}
              isOpen={true}
              onClose={() => setShowHistory(false)}
              onSelectSession={handleSelectSessionFromHistory}
              {...projectSidebarConfig}
            />
          </div>
        ) : (
          <>
            {showHistory && (
              <HistoryModal
                aiAssistantName={AIAssistantName}
                ref={historyModalRef}
                isOpen={showHistory}
                onClose={() => setShowHistory(false)}
                onSelectSession={handleSelectSessionFromHistory}
                {...historyModalConfig}
              />
            )}
          </>
        )}
        <div
          className={clsx(
            "flex flex-col",
            isExpanded ? "flex-1 h-auto w-[100px]" : "flex-1 h-[100dvh] w-auto"
          )}
          {...dropZoneProps}
        >
          <div className={`${styles["header"]} h-[8vh] sm:h-[10vh]`}>
            {!isExpanded && (
              <div className={styles["logo_chatbot"]}>
                <div
                  className={clsx(
                    "relative w-12 h-12",
                    logoVideoUrl
                      ? "sm:w-12 sm:h-12 rounded-full overflow-hidden"
                      : "sm:w-auto sm:h-auto"
                  )}
                >
                  {logoVideoUrl ? (
                    <img src={logoVideoUrl} alt="Animated GIF" />

                  ) : (
                    <>
                      <img
                        src={aufgangAnimationUrl}
                        alt={aufgangAnimationAlt}
                        className={`${["rotate-animation"]} cursor-pointer`}
                      />
                      <img
                        src={aufMinUrl}
                        alt={aufMinAlt}
                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                      />
                    </>
                  )}
                </div>
                {!isExpanded && (
                  <span className="font-semibold text-[14px] sm:text-lg ms-2 truncate">
                    {AIAssistantName}
                  </span>
                )}
              </div>
            )}
            {/* AI Name and Project Dropdown */}
            <div className="flex items-center justify-end gap-2 flex-1 min-w-0 overflow-visible">
              {/* Project selection dropdown */}
              <div className="flex items-center">
                {/* Button to toggle the dropdown */}
                {isProjectDropdownVisible && (
                  <div className="relative">
                    <div
                      id="ai-project-dropdown-toggle-button"
                      onClick={() => {
                        if (!replyMessage && !isInputDisabled && isProjectChangeIconVisible)
                          setShowDropdown((prev) => !prev);
                      }}
                      className={`px-2 py-2 rounded-xl text-xs sm:text-sm text-table-head font-normal bg-chat-dropdown-bg focus:outline-none  w-[140px] sm:min-w-[220px] sm:max-w-[220px] flex justify-between items-center ${replyMessage || isInputDisabled || !isProjectChangeIconVisible
                        ? "pointer-events-none"
                        : "cursor-pointer"
                        }`}
                        style={ replyMessage || isInputDisabled || !isProjectChangeIconVisible ? {backgroundColor: "#b3b3b3", color: "#ffffff"} : {} }
                    >
                      <span>
                        {dropdownSelectedProjectId ? (
                          <EllipsisText
                            text={findProjectNameById(dropdownSelectedProjectId)}
                            length={18}
                          />
                        ) : (
                          defaultProjectPlaceholder
                        )}
                      </span>
                      <span className="ml-2">
                        {showDropdown ? (
                          <FaChevronUp size={9} />
                        ) : (
                          <FaChevronDown size={9} />
                        )}
                      </span>
                    </div> 
                                    
                    {/* Show dropdown only when toggled */}
                    {showDropdown && !replyMessage && !isInputDisabled && isProjectChangeIconVisible && (
                      <div className="absolute right-14 sm:right-0 top-full mt-0 sm:mt-1 w-full z-50">
                        <ProjectDropdown
                          AIAssistantName={AIAssistantName}
                          showDropdown={showDropdown}
                          setShowDropdown={setShowDropdown}
                          agentData={agentData}
                          onChange={(projectId, _projectName) => {
                            setDropdownSelectedProjectId(projectId);
                            if (sessionIsOn) {
                              setSessionIsOn(false);
                              clearAndSetChatMessages();
                              setSelectedSessionId("");
                              setShowNavigationArrows(false);
                            }
                            setIsWebEnabled(false);
                          }}
                          initialProjectId={
                            dropdownSelectedProjectId ?? undefined
                          }
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className={styles["headerButtons"]}>
                  <div className="ml-0 sm:ml-3 flex items-center">
                    {isExpandableButtonEnable && (
                      isExpanded ? (
                        <button
                          onClick={handleExpand}
                          title="Minimize"
                          className={`${isExpanded ? "block" : "hidden"}`}
                        >
                          <img
                            src={nonExpandIconUrl}
                            alt="close icon"
                            className={`${styles["refreshIcon"]}`}
                          />
                        </button>
                      ) : (
                        <button
                          onClick={handleExpand}
                          title="Expand"
                          style={{ cursor: 'pointer' }}
                        >
                          <img
                            src={openInFullIconUrl}
                            alt="close icon"
                            className={`${"refreshIcon"}`}
                          />
                        </button>
                      )
                    )}

                    {!isExpanded && isHistoryIconVisible && (
                      <button
                        className={`ml-3 ${replyMessage || isInputDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                        onClick={handleShowHistory}
                        disabled={!!replyMessage || isInputDisabled}
                        title="View Chat History"
                        style={{ cursor: 'pointer' }}
                      >
                        <img
                          src={historyIconUrl}
                          alt="Refresh"
                          className={`w-6 sm:w-full ${styles["refreshIcon"]}`}
                        />
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setIsExpanded(false);
                      onClose();
                    }}
                    title="Close"
                    style={{ cursor: 'pointer' }}
                  >
                    <img
                      src={chatCloseIconUrl}
                      alt="Close"
                      className={`w-8 sm:w-full ${styles["closeIcon"]}`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
          {showNavigationArrows && (
            <div className="w-full h-12 flex items-center justify-end px-4">
              <div className="flex items-center gap-2">
                <span className="flex items-center text-black text-sm">
                  {totalSessionCount
                    ? `${currentIndexInSession} of ${totalSessionCount}`
                    : "0 of 0"}
                </span>
                <button
                  type="button"
                  className={`rounded-full border border-gray-300 bg-white hover:bg-gray-100 
                    disabled:opacity-50 ${currentIndexInSession === totalSessionCount || isInputDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() => handleNavigate("down")}
                  title="Previous messages"
                  disabled={
                    currentIndexInSession === totalSessionCount ||
                    isInputDisabled
                  }
                >
                  <img
                    src={chatbotDownArrowUrl}
                    alt="Down Arrow"
                    className="w-7"
                  />
                </button>
                <button
                  type="button"
                  className={`rounded-full border border-gray-300 bg-white hover:bg-gray-100 
                    disabled:opacity-50 ${currentIndexInSession <= 1 || isInputDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() => handleNavigate("up")}
                  title="Next messages"
                  disabled={currentIndexInSession <= 1 || isInputDisabled}
                >
                  <img src={chatbotUpArrowUrl} alt="Up Arrow" className="w-7" />
                </button>
                <button
                  type="button"
                  className={`ml-2 text-lg text-gray-500 hover:text-gray-800 focus:outline-none ${isInputDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                  title="Close navigation"
                  onClick={() => {
                    if (!isInputDisabled) {
                      setShowNavigationArrows(false);
                      refreshChatMessages();
                      setSelectedSessionId("");
                      setSessionIsOn(false);
                      useChatMessageStore.getState().setChatMessages(messages);
                      setDropdownSelectedProjectId(null);
                    }
                  }}
                  disabled={isInputDisabled}
                >
                  ×
                </button>
                {isNavigating && (
                  <span className="ml-2 text-xs text-gray-400">Loading...</span>
                )}
                {navigationError && (
                  <span className="ml-2 text-xs text-red-400">
                    Navigation error
                  </span>
                )}
              </div>
            </div>
          )}

          {showSuggestions ? (
            <div
              className={styles["body"]}
              style={{ flex: isExpanded ? 1 : "80vh" }}
            >
              <>
                <WorkWebToggle
                  isInputDisabled={isInputDisabled}
                  IsWebEnabled={IsWebEnabled}
                  setIsWebEnabled={setIsWebEnabled}
                  workWebToggleActiveBgColor={workWebToggleActiveBgColor}
                  workWebToggleInactiveTextColor={workWebToggleInactiveTextColor}
                  workWebToggleTextColor={workWebToggleTextColor}
                  workLabel={workLabel}
                  webLabel={webLabel}
                  onWebClick={() => setDropdownSelectedProjectId(null)}
                  isWebAllowed={isWebAllowed}
                />
                {
                  customSuggestionComponent ? (customSuggestionComponent) : <div className={styles["message"]}>
                    <p className={styles["greeting"]}>Hi {user?.user_name || ""},</p>
                    <p className={styles["question"]}>What would you like to know?</p>
                  </div>
                }
                <div className={styles["suggestions"]}>
                  {currentSuggestions.map((prompt, index) => (
                    <button
                      key={prompt + index}
                      className={`${styles["suggestionCard"]} ${isInputDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                      onClick={() => {
                        if (!isInputDisabled) {
                          setIsWebEnabled(false);
                          handleSuggestionClick(prompt);
                        }
                      }}

                      style={{ border: "1px solid #E6E6E6", borderRadius: "7px", padding: "12px 16px", fontSize: "14px", color: "#232323", textAlign: "left", maxHeight: "300px", display: "flex" }}

                      disabled={isInputDisabled}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
                <div className="flex justify-end items-center gap-2" >
                  <img src={refresh_prompt} style={{ cursor: "pointer" }} alt="Refresh Prompts" />
                  <button
                    style={{ color: "#808080", fontSize: "14px", cursor: "pointer" }}
                    onClick={() => setPage((prevPage) => (prevPage + 1) % Math.ceil(suggestionPrompts.length / suggestionsPerPage))}
                    disabled={isInputDisabled}
                  >
                    Renew Prompts
                  </button>
                </div>
              </>
            </div>
          ) : (
            <>
              {(!dropdownSelectedProjectId || isWorkWebEnableForAll) && (
                <WorkWebToggle
                  isInputDisabled={isInputDisabled}
                  IsWebEnabled={IsWebEnabled}
                  setIsWebEnabled={setIsWebEnabled}
                  workWebToggleActiveBgColor={workWebToggleActiveBgColor}
                  workWebToggleInactiveTextColor={workWebToggleInactiveTextColor}
                  workWebToggleTextColor={workWebToggleTextColor}
                  workLabel={workLabel}
                  onWebClick={() => setDropdownSelectedProjectId(null)}
                  webLabel={webLabel}
                  isWebAllowed={isWebAllowed}
                />
              )}

              <div
                ref={chatContainerRef as React.RefObject<HTMLDivElement>}
                onScroll={handleChatScroll}
                className={`pl-5 pr-2.5 overflow-y-auto mr-1 ${!dropdownSelectedProjectId ? "h-[76vh] sm:h-[72vh]" : "h-[76vh] sm:h-[80vh]"}`}
                style={{ marginBottom: (replyMessage && attachedFiles.length < 0) ? '25px' : '0' }}
              >


                {/* Infinite scroll loader at the top */}
                {chatMessageAppendIsLoading ? (
                  <div className="flex justify-center my-2">
                    <div className="flex items-center gap-2">
                      <svg
                        className="animate-spin h-5 w-5 text-brand-blue"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                      <span className="text-brand-blue text-sm">
                        {loadingMoreMessagesText}
                      </span>
                    </div>
                  </div>
                ) : (
                  // Only show "Load previous messages" if there are messages and topCurrentIndexId exists
                  messages.length > 0 && topCurrentIndexId !== undefined && (
                    <div>
                      <div
                        onClick={() => {
                          if (!isInputDisabled) {
                            appendAtEndChatMessages(topCurrentIndexId, "up");
                          }
                        }}
                        className={`flex items-center justify-center text-gray-500 text-xs sm:text-sm py-1 sm:py-2 font-medium ${isInputDisabled
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer hover:text-brand-blue"
                          }`}
                      >
                        Load previous messages
                      </div>
                    </div>
                  )
                )}

                {/* show loader if chatMessageIsLoading is true */}
                {chatMessageIsLoading ? (
                  <div className="flex justify-center my-2">
                    <Loader />
                  </div>
                ) : (
                  <MessageList
                    messages={messages}
                    user={user}
                    generateInitials={generateInitials}
                    highlightProjectNames={highlightProjectNames}
                    selectedProject={selectedProject}
                    findProjectNameById={findProjectNameById}
                    handleCopy={handleCopy}
                    handleIconMouseEnter={handleIconMouseEnter}
                    handleIconMouseLeave={handleIconMouseLeave}
                    hoveredButton={hoveredButton}
                    copiedMessageId={copiedMessageId}
                    handleReply={handleReply}
                    handleSpeakerClick={handleSpeakerClick}
                    handleDropdownToggle={handleDropdownToggle}
                    openDropdownMessageId={openDropdownMessageId}
                    dropdownRef={dropdownRef}
                    buttonRef={buttonRef}
                    chatContainerRef={chatContainerRef}
                    messagesEndRef={messagesEndRef}
                    showDropdown={showDropdown}
                    setShowDropdown={setShowDropdown}
                    projectsList={projectsList}
                    agentData={agentData}
                    setSelectedProject={setSelectedProject}
                    setOpenDropdownMessageId={setOpenDropdownMessageId}
                    isExpanded={isExpanded}
                    aiTextIconUrl={aiTextIconUrl}
                    isProjectChangeIconVisible={isProjectChangeIconVisible}
                    defaultProjectPlaceholder={defaultProjectPlaceholder}
                    AIAssistantName={AIAssistantName}
                    feedbackBackgroundColor={feedbackBackgroundColor}
                    feedbackBorderColor={feedbackBorderColor}
                    feedbackTextColor={feedbackTextColor}
                    feedbackButtonColor={feedbackButtonColor}
                    feedbackButtonHoverColor={feedbackButtonHoverColor}
                    feedbackPlaceholderColor={feedbackPlaceholderColor}
                    thumbUpIcon={thumbUpIcon}
                    thumbDownIcon={thumbDownIcon}
                  />
                )}

                {sessionIsOn && (
                  <div>
                    {bottomLoading ? (
                      <div className="flex justify-center my-2">
                        <div className="flex items-center gap-2">
                          <svg
                            className="animate-spin h-5 w-5 text-brand-blue"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                            ></path>
                          </svg>
                          <span className="text-brand-blue text-sm">
                            {loadingMoreMessagesText}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <>
                        {!chatMessageError && (
                          <div
                            onClick={() => {
                              if (!isInputDisabled) {
                                loadMoreBottomMessagesHandler(
                                  bottomCurrentIndexId
                                );
                              }
                            }}
                            className={`flex items-center justify-center text-gray-500 text-xs sm:text-sm py-1 sm:py-2 font-medium ${isInputDisabled
                              ? "opacity-50 cursor-not-allowed"
                              : "cursor-pointer hover:text-brand-blue"
                              }`}
                          >
                            {/* {loadMoreMessagesText} */}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                <div ref={messagesEndRef as React.RefObject<HTMLDivElement>} />
              </div>
            </>
          )}

          {
            showScrollButton && (
              isExpanded ?
                <div style={{ marginBottom: (replyMessage !== null && attachedFiles.length === 0) ? '62px' : "20px" }}>
                  <button className="scroll-to-bottom-expanded" onClick={handleScrollClick}>
                    <i className="fa-solid fa-arrow-down"></i>
                  </button>
                </div>
                : <div style={{ marginBottom: (replyMessage !== null && attachedFiles.length === 0) ? '62px' : "20px" }}>
                  <button className="scroll-to-bottom" onClick={handleScrollClick}>
                    <i className="fa-solid fa-arrow-down"></i>
                  </button>
                </div>
            )
          }
          {isDragging && (
              <DragDropOverlay sendButtonColor={sendButtonColor} />
            )}
          <div 
            ref={inputContainerRef}
            className={`${styles["inputContainer"]} ${attachedFiles.length > 0 ? 'pb-2' : ''} relative`}
            style={isDragging ? { 
              borderColor: sendButtonColor,
              backgroundColor: `${sendButtonColor}08`
            } : undefined}
            // {...dropZoneProps}
          >
            {/* Drag and Drop Overlay */}
            
            <div className="relative flex flex-col w-full" style={{ padding: "12px 0" }}>
              {/* Attached Files - Display inside input container above textarea */}
              {attachedFiles.length > 0 && (
                <div className="w-full px-2 mb-2" >
                  <AttachedFilesList
                    files={attachedFiles}
                    onRemove={handleRemoveFile}
                    isInputDisabled={isInputDisabled}
                    replyMessage={replyMessage}
                  />
                </div>
              )}
              <div className="relative flex items-center w-full">
                {/* Reply Message */}
                {replyMessage && (
                  <div>
                    <div className="absolute left-0 w-full bg-[#f9f9f9] text-gray-800 px-2 py-2 pe-8 ps-10 border-dissabled-btn border-b-0 rounded-t-lg flex items-center justify-between -top-12 z-50 overflow-hidden text-ellipsis whitespace-normal h-[45px] reply-chat" style={{ paddingTop: "10px !important" }}>
                      <div style={{ border: "1px solid #CCCCCC", padding: "5px 8px", borderRadius: "8px", width: "100%" }}>
                        <img
                          src={replyChatIconUrl}
                          alt="Reply"
                          className="w-6 h-6 absolute top-0 left-2 mt-3 ml-2"
                        />
                        <span className="flex-1 min-w-0 overflow-hidden text-ellipsis text-xs line-clamp-2" style={{ whiteSpace: "normal", WebkitLineClamp: "2", WebkitBoxOrient: "vertical", display: "-webkit-box", padding: "0px 15px", margin: "0 15px" }}>
                          {/* <Markdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]} // 👈 This allows raw HTML
                      >
                      {String(replyMessage?.content ?? "")}
                      </Markdown> */}
                          {/* <h1>omakr</h1> */}
                          <PreprocessAndRenderLatex
                            content={marked(String(replyMessage?.content ?? ""))}
                            isBlockMath={true}
                            wrapperStyle={{}}
                          />
                        </span>
                      </div>
                    </div>
                    <div className="absolute right-2 bg-transparent text-gray-800 px-1 pb-2 -top-10 z-50 flex items-center justify-between  mt-2">
                      <button
                        onClick={() => !isInputDisabled && closeReply()}
                        className={`bg-transparent border-none ml-2 p-0 leading-none flex items-center z-10 ${isInputDisabled
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                          }`}
                        aria-label="Cancel reply"
                        disabled={isInputDisabled}
                      >
                        <img
                          src={chatCloseIconUrl}
                          alt="Cancel reply"
                          className={`w-6 h-6 ${styles["closeIcon"]}`}
                        />
                      </button>
                    </div>
                  </div>
                )}
                <textarea
                  ref={inputRef2}

                  name="chat-input"
                  placeholder={
                    isListening
                      ? `${listeningPlaceholder}${listeningDots}`
                      : isInputDisabled
                        ? aiThinkingPlaceholder
                        : !IsWebEnabled
                          ? workModePlaceholder
                          : webModePlaceholder
                  }
                  value={inputValue}
                  onChange={handleInputChangeWithRefresh}
                  onKeyDown={!hasFileUploading ? handleInputKeyDown : undefined}
                  className={`${styles["input"]} placeholder:text-gray-500 ${(isInputDisabled || stopReconnectionSocket)
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-text"
                    }`}
                  disabled={isInputDisabled || isListening || stopReconnectionSocket}
                  rows={1}
                />

                <div className="absolute right-2 flex items-center gap-0 mr-1 md:mr-3 ">
                  {/* Attach File */}
                  <div className="relative">
                    <button
                      type="button"
                      className={clsx(
                        styles["inputButton"],
                        isListening && styles["micListening"],
                        {
                          "opacity-50 cursor-not-allowed": isInputDisabled,
                        },
                        "flex items-center justify-center p-0"
                      )}
                      onClick={() => {
                        if (!isInputDisabled) {
                          setShowFileUploadModal((prev) => !prev);
                        }
                      }}
                      disabled={isInputDisabled}
                      tabIndex={-1} // Remove from tab order to prevent accidental focus
                    >
                      <img
                        src={curcular_attach_file}
                        alt="Attach file"
                        className="w-7 h-7"
                      />
                    </button>
                    {/* File Upload Modal */}
                    <FileUploadModal
                      isOpen={showFileUploadModal}
                      onClose={() => setShowFileUploadModal(false)}
                      onFileSelect={handleFileSelect}
                      googleTokenData={googleTokenData}
                      authToken={googleAuthToken}
                      onGoogleTokenSubmit={onGoogleTokenSubmit}
                      onGoogleTokenFetch={onGoogleTokenFetch}
                      onGoogleDriveFileIds={onGoogleDriveFileIds}
                      showGoogleDrive={showGoogleDriveOption}
                    />
                  </div>
                  {/* Mic Button */}
                  <button
                    type="button"
                    className={clsx(
                      styles["inputButton"],
                      isListening && styles["micListening"],
                      {
                        "opacity-50 cursor-not-allowed": isInputDisabled,
                      },
                      "flex items-center justify-center p-0"
                    )}
                    onClick={handleMicClick}
                    onMouseEnter={() => !isInputDisabled && setIsMicHovered(true)}
                    onMouseLeave={() => setIsMicHovered(false)}
                    onKeyDown={(e) => {
                      // Prevent Enter key from triggering mic button
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        // If user presses Enter, send the message instead
                        if (!isInputDisabled && inputValue.trim()) {
                          handleSend();
                        }
                      }
                    }}
                    disabled={isInputDisabled}
                    tabIndex={-1} // Remove from tab order to prevent accidental focus
                  >
                    {isListening ? (
                      isMicHovered ? (
                        <img
                          src={chatMicOffIconUrl}
                          alt="Turn microphone off"
                          className="w-7"
                        />
                      ) : (
                         <img
                          src={chatMicOffIconUrl}
                          alt="Turn microphone off"
                          className="w-7"
                        />
                      )
                    ) : (
                      <img
                        src={chatbotMicIconUrl}
                        alt="Chatbot mic icon"
                        className="w-7"
                      />
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className={`${styles["inputButtons"]}`}>
              <button
                className={`${styles["inputButton"]} ${styles["sendButton"]} ${(inputValue.trim() && !hasFileUploading) && !isInputDisabled && !stopReconnectionSocket
                  ? ""
                  : `${styles["disabledSendButton"]}`
                  }`}
                onClick={handleSend}
                disabled={(!inputValue.trim() && hasUploadedFiles) || isInputDisabled || hasFileUploading || ((!inputValue.trim()) || isInputDisabled || stopReconnectionSocket)}
                //disabled={(!inputValue.trim() && hasUploadedFiles) || isInputDisabled  }

                style={sendButtonStyles}
              >
                <span>
                  <img
                    src={
                      ((!inputValue.trim()) || isInputDisabled || stopReconnectionSocket)
                        ? chatSendIconUrl
                        : chatSendIconWhiteUrl
                    }
                    className={`${styles["sendIcon"]} w-3`}
                    alt="send chat button"
                  />
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BeforeExpand;
